import SelectComponent from "@via/components/select/select.tsx";
import { Bar } from "react-chartjs-2";
import React, { useEffect, useState } from "react";
import { ChartOptions } from "chart.js";
import { RunEstimation } from "common";

export const UsageMetricOptions = [
  { label: "Total Cost", value: "totalCost", unit: "$", color: "#6D9FD5" },
  {
    label: "CPU Time",
    value: "totalCpuTime",
    unit: "CPU⋅min",
    color: "#F3B031",
  },
  {
    label: "Memory Time",
    value: "totalMemoryTime",
    unit: "GB⋅min",
    color: "rgba(125, 86, 255, 0.7)",
  },
  {
    label: "Disk Time",
    value: "totalDiskTime",
    unit: "GB⋅min",
    color: "#4BAE4F",
  },
] as const;

export type UsageMetricOption = (typeof UsageMetricOptions)[number];

interface UsageBarChartsProps {
  runEstimations: RunEstimation[];
  metricOptions?: readonly UsageMetricOption[];
  /** Defaults to 500 */
  heightPx?: number;
}

const UsageBarCharts: React.FC<UsageBarChartsProps> = ({
  runEstimations,
  metricOptions,
  heightPx = 500,
}) => {
  const options = metricOptions ?? UsageMetricOptions;
  const [selectedMetric, setSelectedMetric] =
    useState<UsageMetricOption | null>(options[0] ?? null);

  const estimations = runEstimations;

  // If the available metrics change (e.g. cost gated), reset selection
  useEffect(() => {
    setSelectedMetric(options[0] ?? null);
  }, [options]);

  const chartData = selectedMetric
    ? {
        labels: estimations.map(e =>
          new Date(e.runEndDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          })
        ),
        datasets: [
          {
            label: `${selectedMetric.label} (${selectedMetric.unit})`,
            data: estimations.map(e => e[selectedMetric.value]),
            backgroundColor: selectedMetric.color,
            borderRadius: 6,
            maxBarThickness: 200,
          },
        ],
      }
    : { labels: [], datasets: [] };

  const chartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return selectedMetric?.unit === "$" ? `$${value}` : value;
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
    },
  };

  return (
    <div className="w-full py-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold"></h2>
        <div className="min-w-[160px]">
          <SelectComponent
            name={"metric"}
            enableClear={false}
            placeholder=""
            value={selectedMetric}
            options={options}
            onChange={val => setSelectedMetric(val as any)}
          />
        </div>
      </div>
      <div className="w-full" style={{ height: `${heightPx}px` }}>
        <Bar
          data={chartData}
          options={chartOptions}
          className="w-full h-full"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  );
};

export default UsageBarCharts;
